import { useState, useEffect, useCallback, useRef } from 'react';
import { readAutosave, clearAutosave, useAutosave, useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { Link, useNavigate } from 'react-router-dom';
import {
  Shield, Truck, Camera, ClipboardCheck, AlertTriangle, Flag,
  MessageSquare, Paperclip, Check, X, ChevronRight, LogOut,
  MapPin, Gauge, FileSignature, ArrowRight, Wrench, ListChecks,
} from 'lucide-react';
import { dotApi } from '@/utils/api';
import { isAssignmentForLocalDay } from '@/utils/calendarDate';
import { RepairsPanel } from '@/components/dot/RepairsPanel';

// ============================================================================
// Types
// ============================================================================
interface SnapshotItem {
  item_id: number;
  label: string;
  order: number;
  reg_reference?: string | null;
  citation_links?: number[];
  category: string;
  photo_required: boolean;
  photo_min: number;
  photo_caption_required: boolean;
  photo_guidance?: string | null;
  tire_position?: string;
}

interface ResponseState {
  result: 'PASS' | 'DEFECT' | 'NOT_APPLICABLE' | null;
  photos: string[];
  captions: string[];
  defectDescription?: string;
  affectsSafety?: boolean;
  couldCauseBreakdown?: boolean;
  naReason?: string; // B9: required when result=NOT_APPLICABLE (pending supervisor/admin authorization)
}

interface Vehicle {
  id: number;
  unitNumber: string;
  vehicleType: string;
  status: string;
  dispatchHold: string;
  dispatchHoldReason?: string;
}

interface Assignment {
  id: number;
  vehicleId: number;
  driverId: number;
  shiftDate: string;
  vehicle: Vehicle;
  revokedAt: string | null;
}

interface DraftResult {
  dvir: { id: number; snapshotItemCount: number; status: string; checklistVersionId: number };
  snapshotItems: SnapshotItem[];
  snapshotItemCount: number;
}

// B1: composite key helper — responses are keyed by `${itemId}|${tirePosition ?? ''}`
function rkey(itemId: number, tirePosition?: string): string {
  return `${itemId}|${tirePosition ?? ''}`;
}

/**
 * Luna audit DOT-2 (2026-10-06): date-only DVIR shift dates are stored as UTC
 * midnight ('2026-10-01T00:00:00.000Z'). `new Date(...).toLocaleDateString()`
 * shifted that instant to the previous calendar day in US timezones
 * (2026-10-01 UTC → 9/30/2026 in Indiana). Render the RECORDED UTC calendar
 * day without any timezone shift; fall back to locale rendering only when the
 * value is not a recognizable date string.
 */
function formatRecordedDay(value: any): string {
  if (value == null) return '';
  let raw: string;
  try {
    raw = typeof value === 'string' ? value : new Date(value).toISOString();
  } catch {
    return '';
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (m) {
    return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).toLocaleDateString('en-US', {
      timeZone: 'UTC',
    });
  }
  return new Date(value).toLocaleDateString();
}

// ============================================================================
// Component
// ============================================================================

export function DotInspectionPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [tab, setTab] = useState<'PRE_TRIP' | 'POST_TRIP' | 'REPAIRS'>('PRE_TRIP');
  const [companyVehicles, setCompanyVehicles] = useState<{ id: number; unitNumber: string }[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [priorDvir, setPriorDvir] = useState<any>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  // S1 Bug 1: split the local checkbox state (ackChecked) from the post-sign
  // state (acknowledged). The checkbox previously SET `acknowledged`, which
  // unmounted the whole acknowledgment block (including its Sign button) the
  // moment it was ticked — a pre-trip dead end.
  const [ackChecked, setAckChecked] = useState(false);
  const [acknowledgesSafe, setAcknowledgesSafe] = useState(false);
  const [draft, setDraft] = useState<DraftResult | null>(null);
  const [responses, setResponses] = useState<Record<string, ResponseState>>({}); // B1: keyed by composite key
  const [odometer, setOdometer] = useState<number | ''>('');
  const [gps, setGps] = useState<{ lat?: number; lng?: number; accuracy?: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signResult, setSignResult] = useState<any>(null);
  const [activeDrawer, setActiveDrawer] = useState<{ itemId: number; tirePosition?: string; type: 'defect' | 'flag' | 'comment' | 'action' | 'repair' } | null>(null);
  const [commentText, setCommentText] = useState('');
  const [findingForm, setFindingForm] = useState({ title: '', notes: '', closureComments: '' });
  const [concernNoteText, setConcernNoteText] = useState(''); // B13: concern note state
  const [defectIds, setDefectIds] = useState<Record<string, number>>({}); // B12: track defect IDs by composite key
  // B12: Action-item form state (controlled inputs + submit handler)
  const [actionItemForm, setActionItemForm] = useState({ correctiveAction: '', deadline: '', responsiblePersonId: '' });
  // B12: Repair-clearance form state (controlled inputs + submit handler)
  const [repairForm, setRepairForm] = useState({
    repairDescription: '', partsUsed: '', closureComments: '',
    disposition: 'REPAIRED' as 'REPAIRED' | 'REPAIR_UNNECESSARY' | 'NOT_CLEARED',
    mechanicAttestationText: '', mechanicAttestsVehicleSafe: false, repairPhotoKeys: [] as string[],
  });
  // B14: track N/A items that have been authorized in-session (supervisor/admin
  // clicks "Authorize N/A" per item) so the UI reflects authorization state
  // without a full server re-fetch.
  const [naAuthorizedItems, setNaAuthorizedItems] = useState<Record<string, boolean>>({});
  // B14: when true, a driver's sign attempt found unauthorized N/A items — the
  // DVIR is held in DRAFT pending supervisor/admin authorization.
  const [naPendingAuth, setNaPendingAuth] = useState(false);
  // S1: in-progress checklist autosave — backing out no longer loses answers.
  const [dotDraftRestored, setDotDraftRestored] = useState(false);
  const dotDraftKey = draft ? `conformara-dot-draft-${draft.dvir.id}` : null;
  useAutosave(dotDraftKey ?? 'conformara-dot-draft-none', { responses, odometer }, !!dotDraftKey);
  const dotDirty = draft && Object.values(responses).some((r) => r.result !== null);
  useUnsavedGuard(!!dotDirty);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({}); // B12: hidden file input refs for photo upload

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    } else {
      navigate('/login');
      return;
    }
    loadAssignments();
    // Repairs tab needs the company's vehicles for unit labels / new-order picker
    dotApi.listVehicles().then((res: any) => setCompanyVehicles((res.data || []).map((v: any) => ({ id: v.id, unitNumber: v.unitNumber })))).catch(() => {});
  }, [navigate]);

  const loadAssignments = async () => {
    try {
      const res = await dotApi.listAssignments();
      // Only today's, non-revoked. LUNA18-DOT-003: compare the RECORDED
      // calendar day of the date-only shift date against the user's LOCAL
      // calendar day — the old UTC-day comparison hid same-day assignments
      // from UTC-negative users after UTC midnight (Indiana evening: local
      // Oct 9 = UTC Oct 10). Never a `<=` past-window, which resurrected
      // stale prior-day assignments (Delta11-001).
      const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const todays = (res.data || []).filter((a: Assignment) => !a.revokedAt && isAssignmentForLocalDay(a.shiftDate, new Date(), localTz));
      setAssignments(todays);
    } catch (err) {
      console.error('Error loading assignments', err);
      // DEMO-QA re-triage: actionable copy — say WHY this fails and HOW to fix
      // it (distinct from a genuinely empty assignment list below).
      setError(
        'Could not load your vehicle assignments. If you are not a registered driver, ask your operations lead to designate you and assign a vehicle.'
      );
    }
  };

  // --- Acknowledgment step (pre-trip only) ---
  // B13: concern note is always visible on the refusal path; collected via state, not DOM lookup
  const signAcknowledgment = async (refusal: boolean) => {
    if (!selectedAssignment) return;
    if (refusal && !concernNoteText.trim()) {
      setError('A concern note is required for a refusal.');
      return;
    }
    const noPriorReport = !!priorDvir?.no_prior_report;
    const attestationText = refusal
      ? 'I decline to operate this vehicle and report a safety concern.'
      : noPriorReport
        ? 'No prior report exists for this vehicle — I confirm the vehicle is in safe operating condition.'
        : 'I have reviewed the prior inspection report and am satisfied the vehicle is in safe operating condition.';
    try {
      setLoading(true);
      await dotApi.signAcknowledgment({
        vehicleId: selectedAssignment.vehicleId,
        priorDvirId: priorDvir?.id ?? null,
        noPriorReportReason: noPriorReport ? 'no_prior_report' : null,
        attestationText,
        attestationVersion: 'v1',
        // S1 Bug 2: with no prior report there is nothing to have reviewed —
        // send false + a reason; the server only accepts false when
        // priorDvirId is null AND a noPriorReportReason is present.
        acknowledgesReviewedPriorDvir: !noPriorReport,
        refusal,
        concernNote: refusal ? concernNoteText : null,
      });
      if (refusal) {
        setSignResult({ refusal: true, message: 'Refusal recorded. Concern routed to carrier review. Vehicle status unchanged.' });
        setSelectedAssignment(null);
      } else {
        setAcknowledged(true);
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || 'Acknowledgment failed';
      // UAT-017: a blocked driver must see WHY and HOW to fix it, not a bare
      // generic error — the server messages now name the field and remedy;
      // a permission rejection also gets the grant path spelled out here.
      setError(status === 403
        ? `${msg} How to fix: ask an admin to grant your role the 'ack.sign' permission (Permissions Matrix), or have a role that holds it sign the acknowledgement.`
        : msg);
    } finally {
      setLoading(false);
    }
  };

  // --- Create draft (starts checklist) ---
  const startChecklist = async () => {
    if (!selectedAssignment) return;
    try {
      setLoading(true);
      setError(null);
      const res = await dotApi.createDraft({ assignmentId: selectedAssignment.id, type: tab === 'REPAIRS' ? 'PRE_TRIP' : tab, odometerMi: typeof odometer === 'number' ? odometer : undefined, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
      setDraft(res.data);
      // B1: init responses keyed by composite key `${itemId}|${tirePosition}`
      const init: Record<string, ResponseState> = {};
      for (const item of res.data.snapshotItems) {
        init[rkey(item.item_id, item.tire_position)] = { result: null, photos: [], captions: [] };
      }
      // S1: restore an in-progress draft (backing out mid-checklist no longer
      // loses answers — responses + odometer autosave per DVIR id).
      const saved = readAutosave<{ responses: Record<string, ResponseState>; odometer: number | '' }>(`conformara-dot-draft-${res.data.dvir.id}`);
      if (saved?.responses) {
        for (const key of Object.keys(init)) {
          if (saved.responses[key]) init[key] = { ...init[key], ...saved.responses[key] };
        }
        if (typeof saved.odometer === 'number') setOdometer(saved.odometer);
        setDotDraftRestored(true);
      }
      setResponses(init);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create draft');
    } finally {
      setLoading(false);
    }
  };

  // --- Set result for a question ---
  const setResult = (itemId: number, tirePosition: string | undefined, result: ResponseState['result']) => {
    const key = rkey(itemId, tirePosition);
    setResponses((prev) => ({
      ...prev,
      [key]: { ...prev[key], result },
    }));
  };

  // --- B12/B14: Add photo (REAL upload — not simulated) ---
  const [photoUploading, setPhotoUploading] = useState(false);
  const addPhoto = async (itemId: number, tirePosition?: string) => {
    if (!draft) return;
    // B12: trigger the hidden file input for this item
    const key = rkey(itemId, tirePosition);
    const input = fileInputRefs.current[key];
    if (input) {
      input.click();
    }
  };

  // B12/B14: handle file selection → real upload → attach to response
  const handlePhotoSelect = async (itemId: number, tirePosition: string | undefined, event: React.ChangeEvent<HTMLInputElement>) => {
    if (!draft) return;
    const file = event.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      // B12: real upload — server stores bytes and returns a real object key
      const uploadRes = await dotApi.uploadPhoto(draft.dvir.id, file);
      const key = uploadRes.data.objectKey;
      // B12: blocking attach — fails loudly if attachment doesn't succeed
      await dotApi.attachEvidence(draft.dvir.id, itemId, key, tirePosition);
      const rk = rkey(itemId, tirePosition);
      setResponses((prev) => ({
        ...prev,
        [rk]: { ...prev[rk], photos: [...(prev[rk]?.photos || []), key] },
      }));
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Photo upload failed — evidence is required');
    } finally {
      setPhotoUploading(false);
      // reset the input so the same file can be selected again
      event.target.value = '';
    }
  };

  // --- Set caption for a photo ---
  const setCaption = (itemId: number, tirePosition: string | undefined, index: number, caption: string) => {
    const key = rkey(itemId, tirePosition);
    setResponses((prev) => {
      const captions = [...(prev[key]?.captions || [])];
      captions[index] = caption;
      return { ...prev, [key]: { ...prev[key], captions } };
    });
  };

  // --- Add defect via drawer ---
  const submitDefect = async (itemId: number, tirePosition?: string) => {
    const key = rkey(itemId, tirePosition);
    const r = responses[key];
    if (!r.defectDescription?.trim()) {
      setError('Defect description is required.');
      return;
    }
    try {
      setLoading(true);
      const res = await dotApi.addDefect(draft!.dvir.id, {
        inspectionItemId: itemId,
        tirePosition,
        description: r.defectDescription,
        affectsSafety: !!r.affectsSafety,
        couldCauseBreakdown: !!r.couldCauseBreakdown,
      });
      // B12: track the defect ID for later finding creation
      setDefectIds((prev) => ({ ...prev, [key]: res.data.id }));
      setResult(itemId, tirePosition, 'DEFECT');
      setActiveDrawer(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to add defect');
    } finally {
      setLoading(false);
    }
  };

  // --- Add comment (B5: checklistVersionId required when DVIR context given) ---
  const submitComment = async (itemId: number) => {
    if (!commentText.trim()) return;
    try {
      await dotApi.addComment(itemId, commentText, draft?.dvir.id, draft?.dvir.checklistVersionId);
      setCommentText('');
      setActiveDrawer(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to add comment');
    }
  };

  // --- B12: Create finding (flag) — always creates a DVIRDefect first, then uses the correct defectId ---
  const submitFinding = async (itemId: number, tirePosition?: string) => {
    try {
      setLoading(true);
      const dKey = rkey(itemId, tirePosition);
      let defectId = defectIds[dKey];
      // B12: If no defect exists yet, create one (the PRD requires a Flag→DVIRDefect first)
      if (!defectId) {
        const r = responses[dKey];
        const desc = r?.defectDescription?.trim() || findingForm.title;
        if (!desc) {
          setError('A defect description or finding title is required before flagging.');
          setLoading(false);
          return;
        }
        const res = await dotApi.addDefect(draft!.dvir.id, {
          inspectionItemId: itemId,
          tirePosition,
          description: desc,
          affectsSafety: false,
          couldCauseBreakdown: false,
        });
        defectId = res.data.id;
        setDefectIds((prev) => ({ ...prev, [dKey]: defectId! }));
      }
      // Now create the finding using the correct defectId
      await dotApi.createFinding(defectId, {
        title: findingForm.title,
        notes: findingForm.notes,
        closureComments: findingForm.closureComments || undefined,
      });
      setActiveDrawer(null);
      setFindingForm({ title: '', notes: '', closureComments: '' });
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create finding');
    } finally {
      setLoading(false);
    }
  };

  // --- B14: Supervisor/admin authorizes a single N/A response ---
  // The driver does NOT call this — only a supervisor/admin user can invoke
  // the authorize-na endpoint.  This wires the role-separated authorize step
  // so the driver never hits a 403 on the normal submit path.
  const authorizeNa = async (itemId: number, tirePosition?: string) => {
    if (!draft) return;
    const rk = rkey(itemId, tirePosition);
    const naReason = responses[rk]?.naReason?.trim();
    if (!naReason) {
      setError('N/A reason is required before authorization.');
      return;
    }
    try {
      setLoading(true);
      await dotApi.authorizeNotApplicable(draft.dvir.id, {
        inspectionItemId: itemId,
        tirePosition,
        naReason,
      });
      setNaAuthorizedItems((prev) => ({ ...prev, [rk]: true }));
      setNaPendingAuth(false);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'N/A authorization failed');
    } finally {
      setLoading(false);
    }
  };

  // --- B11: Sign with attestation text + server-returned timestamp ---
  const sign = async () => {
    if (!draft) return;
    const answeredCount = Object.values(responses).filter((r) => r.result !== null).length;
    if (answeredCount !== draft.snapshotItemCount) {
      setError(`All ${draft.snapshotItemCount} items must be answered (no bulk-pass). Answered: ${answeredCount}`);
      return;
    }
    // B11: Validate mandatory photos/captions before sign (also enforced server-side)
    // B1: use composite key for each snapshot item
    for (const item of draft.snapshotItems) {
      const rk = rkey(item.item_id, item.tire_position);
      const r = responses[rk];
      if (!r || r.result === null) {
        setError(`"${item.label}" must be answered.`);
        return;
      }
      if (item.photo_required && (r.photos?.length || 0) < item.photo_min) {
        setError(`"${item.label}" requires ${item.photo_min} photo(s); got ${r.photos?.length || 0}.`);
        return;
      }
      if (item.photo_caption_required && (r.captions?.filter((c) => c && c.trim()).length || 0) < item.photo_min) {
        setError(`"${item.label}" requires ${item.photo_min} non-empty caption(s)/reading(s).`);
        return;
      }
    }
    // Save responses first
    try {
      setLoading(true);
      setError(null);
      // B1: Build payload with tirePosition for each response (composite key round-trip)
      const payload = draft.snapshotItems.map((item) => {
        const rk = rkey(item.item_id, item.tire_position);
        const r = responses[rk];
        return {
          inspectionItemId: item.item_id,
          tirePosition: item.tire_position ?? undefined, // B1: round-trip tirePosition
          result: r.result,
          photoObjectKeys: r.photos,
          photoCaptions: r.captions,
          // B9: send N/A reason from user input (no fake fallback)
          naReason: r.result === 'NOT_APPLICABLE' ? r.naReason : undefined,
        };
      });
      await dotApi.upsertResponses(draft.dvir.id, payload);
      // B14: N/A authorize-then-sign — role-separated, NO 403 on the driver path.
      // The driver does NOT call the supervisor/admin-only authorize-na endpoint.
      // Instead: (a) if the current user is a supervisor/admin, authorize each
      // N/A item here (they have permission) then inform the driver can sign;
      // (b) if the current user is a driver, the DVIR is HELD in DRAFT pending
      // authorization — the reason travels with the saved responses.  The driver
      // falls through to the sign attempt; the server's sign transaction
      // independently verifies naAuthorizedBy + authorizer role and either
      // succeeds (if a supervisor/admin already authorized in another session)
      // or returns a clear 400 error (no 403).
      const naItems = draft.snapshotItems.filter((item) => {
        const rk = rkey(item.item_id, item.tire_position);
        return responses[rk]?.result === 'NOT_APPLICABLE';
      });
      // S1 Bug 3: roles are UPPERCASE enums in storage — the old lowercase
      // literals ('supervisor'/'admin') never matched, so the N/A Authorize
      // control never rendered. Match the same literals the rest of the
      // codebase uses (MANAGEMENT_ROLES).
      const isSupervisorOrAdmin = ['ADMIN', 'SAFETY_MANAGER', 'CONSTRUCTION_SUPERVISOR', 'PROJECT_MANAGER', 'CONSTRUCTION_MANAGER'].includes(user?.role);
      if (naItems.length > 0 && isSupervisorOrAdmin) {
        // B14: Supervisor/admin — authorize each N/A item (they have permission).
        for (const item of naItems) {
          const rk = rkey(item.item_id, item.tire_position);
          const naReason = responses[rk]?.naReason?.trim() || undefined;
          await dotApi.authorizeNotApplicable(draft.dvir.id, {
            inspectionItemId: item.item_id,
            tirePosition: item.tire_position,
            naReason,
          });
          setNaAuthorizedItems((prev) => ({ ...prev, [rk]: true }));
        }
        // Supervisor/admin cannot sign (driver-only endpoint).  Inform the user
        // that the assigned driver can now sign.
        setError('N/A items authorized. The assigned driver can now sign the DVIR.');
        setLoading(false);
        return;
      }
      if (naItems.length > 0 && !isSupervisorOrAdmin) {
        // B14: Driver — do NOT call authorize-na (avoids 403).  Mark pending.
        // The DVIR stays in DRAFT (editable).  The reason is saved with the
        // responses above.  Fall through to the sign attempt — the server
        // verifies naAuthorizedBy and either succeeds (if already authorized
        // by a supervisor/admin in another session) or returns a clear 400.
        setNaPendingAuth(true);
      } else {
        setNaPendingAuth(false);
      }
      // Capture GPS (best-effort)
      let gpsData: any = { locationSource: 'UNAVAILABLE' };
      try {
        const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        });
        gpsData = { gpsLat: pos.coords.latitude, gpsLng: pos.coords.longitude, gpsAccuracyM: pos.coords.accuracy, locationSource: 'DEVICE' };
        setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
      } catch {
        gpsData = { locationSource: 'DENIED' };
      }
      // B11: send attestation text to server; server sets signedAt
      const signRes = await dotApi.signDvir(draft.dvir.id, {
        odometerMi: typeof odometer === 'number' ? odometer : undefined,
        ...gpsData,
        clientDeviceId: 'web-client',
        attestationText: 'I attest this DVIR is accurate to the best of my knowledge.',
        attestationVersion: 'v1',
      });
      // B11: use server-returned signedAt, not client-rendered time
      setSignResult(signRes.data);
      if (dotDraftKey) clearAutosave(dotDraftKey);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Sign failed');
    } finally {
      setLoading(false);
    }
  };

  // --- B12: Submit action item (Path B) — calls real API with controlled inputs ---
  const submitActionItem = async (itemId: number, tirePosition?: string) => {
    const dKey = rkey(itemId, tirePosition);
    let defectId = defectIds[dKey];
    if (!defectId) {
      setError('A defect must be reported before creating an action item.');
      return;
    }
    if (!actionItemForm.correctiveAction.trim() || !actionItemForm.deadline || !actionItemForm.responsiblePersonId) {
      setError('Corrective action, deadline, and responsible person ID are all required.');
      return;
    }
    try {
      setLoading(true);
      await dotApi.createActionItem(defectId, {
        correctiveAction: actionItemForm.correctiveAction,
        deadline: actionItemForm.deadline,
        responsiblePersonId: Number(actionItemForm.responsiblePersonId),
      });
      setActionItemForm({ correctiveAction: '', deadline: '', responsiblePersonId: '' });
      setActiveDrawer(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create action item');
    } finally {
      setLoading(false);
    }
  };

  // --- B12/B14: Submit repair clearance — calls real API with repair photo upload ---
  const [repairFileRef, setRepairFileRef] = useState<React.RefObject<HTMLInputElement>>({ current: null });
  const submitRepairClearance = async (itemId: number, tirePosition?: string) => {
    const dKey = rkey(itemId, tirePosition);
    let defectId = defectIds[dKey];
    if (!defectId) {
      setError('A defect must be reported before recording a repair clearance.');
      return;
    }
    if (!repairForm.repairDescription.trim()) {
      setError('Repair description is required.');
      return;
    }
    if (!repairForm.mechanicAttestationText.trim()) {
      setError('Mechanic attestation text is required.');
      return;
    }
    if (repairForm.repairPhotoKeys.length === 0) {
      setError('At least one repair photo is required.');
      return;
    }
    try {
      setLoading(true);
      await dotApi.recordRepairClearance(defectId, {
        repairDescription: repairForm.repairDescription,
        partsUsed: repairForm.partsUsed || undefined,
        repairPhotoObjectKeys: repairForm.repairPhotoKeys,
        closureComments: repairForm.closureComments || undefined,
        disposition: repairForm.disposition,
        mechanicAttestationText: repairForm.mechanicAttestationText,
        mechanicAttestsVehicleSafe: repairForm.mechanicAttestsVehicleSafe,
      });
      setRepairForm({
        repairDescription: '', partsUsed: '', closureComments: '',
        disposition: 'REPAIRED', mechanicAttestationText: '', mechanicAttestsVehicleSafe: false, repairPhotoKeys: [],
      });
      setActiveDrawer(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to record repair clearance');
    } finally {
      setLoading(false);
    }
  };

  // B12/B14: handle repair photo file selection → real upload
  const handleRepairPhotoSelect = async (dvirId: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const uploadRes = await dotApi.uploadPhoto(dvirId, file);
      const key = uploadRes.data.objectKey;
      setRepairForm((prev) => ({ ...prev, repairPhotoKeys: [...prev.repairPhotoKeys, key] }));
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Repair photo upload failed');
    } finally {
      setPhotoUploading(false);
      event.target.value = '';
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const selectAssignment = async (a: Assignment) => {
    setSelectedAssignment(a);
    setAcknowledged(false);
    setAckChecked(false);
    setDraft(null);
    setResponses({});
    setSignResult(null);
    setError(null);
    if (tab === 'PRE_TRIP') {
      try {
        const res = await dotApi.getPriorDvir(a.vehicleId);
        setPriorDvir(res.data);
      } catch {
        setPriorDvir({ no_prior_report: true });
      }
    }
  };

  const answeredCount = Object.values(responses).filter((r) => r.result !== null).length;
  // B11: canSign checks answered count AND mandatory photos/captions (B1: composite keys)
  const allPhotosValid = draft ? draft.snapshotItems.every((item) => {
    const rk = rkey(item.item_id, item.tire_position);
    const r = responses[rk];
    if (!r || r.result === null) return false;
    if (item.photo_required && (r.photos?.length || 0) < item.photo_min) return false;
    if (item.photo_caption_required && (r.captions?.filter((c) => c && c.trim()).length || 0) < item.photo_min) return false;
    return true;
  }) : false;
  const canSign = draft && answeredCount === draft.snapshotItemCount && allPhotosValid;

  // ============================================================================
  // RENDER
  // ============================================================================
  if (!user) return null;

  if (signResult?.refusal) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-lg text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Refusal Recorded</h2>
          <p className="text-gray-600 mb-4">{signResult.message}</p>
          <p className="text-xs text-gray-400 italic mb-4">
            Conformara records inspection events as reported. It does not certify compliance with 49 CFR or any other regulation.
          </p>
          <button onClick={() => { setSignResult(null); setSelectedAssignment(null); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Back to vehicles</button>
        </div>
      </div>
    );
  }

  if (signResult) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-lg text-center">
          <Check className="h-12 w-12 text-green-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">DVIR Signed</h2>
          <p className="text-gray-600 mb-2">Hash: <code className="bg-gray-100 px-1 rounded">{(signResult.hash || '').slice(0, 16)}…</code></p>
          <p className="text-gray-600 mb-4">Signed at (server): {signResult.dvir?.signedAt ? new Date(signResult.dvir.signedAt).toLocaleString() : '—'}</p>
          <p className="text-xs text-gray-400 italic mb-4">
            Conformara records inspection events as reported. It does not certify compliance with 49 CFR or any other regulation.
          </p>
          <button onClick={() => { setSignResult(null); setDraft(null); setSelectedAssignment(null); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Truck className="h-8 w-8 text-blue-600" />
            <span className="text-xl font-bold text-gray-900">DOT Inspection Log</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-sm text-gray-600 hover:text-gray-900">Dashboard</Link>
            <button onClick={handleLogout} className="flex items-center gap-1 text-sm text-gray-700 hover:text-gray-900">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Disclaimer banner (PRD §0) */}
      <div className="bg-amber-50 border-b border-amber-200">
        <div className="max-w-5xl mx-auto px-6 py-2 text-xs text-amber-800 text-center">
          Conformara records inspection events as reported. It does not certify compliance with 49 CFR or any other regulation.
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 py-6">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            {/* Fix 5 (UAT): DVIR-creation 409 gets actionable copy pointing admins
                at the checklist admin flow. */}
            <span>
              {error}
              {/No published checklist/i.test(error) && (
                <span className="block mt-1 font-medium">
                  This vehicle type has no published checklist. Ask an admin to add inspection items and publish a checklist version.
                </span>
              )}
            </span>
            <button onClick={() => setError(null)} className="ml-auto"><X className="h-4 w-4" /></button>
          </div>
        )}

        {/* Tabs */}
        {!draft && (
          <div className="flex gap-2 mb-6">
            {(['PRE_TRIP', 'POST_TRIP', 'REPAIRS'] as const).map((t) => (
              <button key={t} onClick={() => { setTab(t); if (t !== 'REPAIRS') setSelectedAssignment(null); }}
                className={`px-4 py-2 rounded-lg font-medium ${tab === t ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border'}`}>
                {t === 'PRE_TRIP' ? 'Pre-Trip' : t === 'POST_TRIP' ? 'Post-Trip' : 'Repairs'}
              </button>
            ))}
          </div>
        )}

        {/* Repairs tab — uniform repair-documentation workflow (v0.4 §C.5) */}
        {tab === 'REPAIRS' && !draft && <RepairsPanel user={user} vehicles={companyVehicles} />}

        {/* Step 1: Vehicle select */}
        {!selectedAssignment && !draft && tab !== 'REPAIRS' && (
          <div>
            <h2 className="text-lg font-semibold mb-4">Select a vehicle for today's {tab === 'PRE_TRIP' ? 'pre-trip' : 'post-trip'} inspection</h2>
            {/* DEMO-QA re-triage: the friendly empty state renders ONLY when the
                list genuinely loaded empty — on a load failure just the error
                banner above shows (no stacked "no vehicles" message). */}
            {!error && assignments.length === 0 ? (
              <div><p className="text-gray-500">No vehicles assigned to you today. Ask your operations lead to assign a vehicle.</p><Link to="/fleet" className="text-blue-700 underline">Open fleet setup</Link></div>
            ) : assignments.length > 0 ? (
              <div className="grid gap-3">
                {assignments.map((a) => (
                  <button key={a.id} onClick={() => selectAssignment(a)}
                    className="bg-white p-4 rounded-lg shadow hover:shadow-md border border-gray-100 flex items-center justify-between text-left">
                    <div>
                      <div className="font-semibold text-gray-900">Unit {a.vehicle.unitNumber}</div>
                      <div className="text-sm text-gray-500">{a.vehicle.vehicleType}</div>
                      {a.vehicle.dispatchHold !== 'NONE' && (
                        <span className="inline-block mt-1 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                          Dispatch hold pending carrier review: {a.vehicle.dispatchHoldReason || a.vehicle.dispatchHold}
                        </span>
                      )}
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        )}

        {/* Step 2: Prior DVIR review + acknowledgment (pre-trip only) */}
        {selectedAssignment && tab === 'PRE_TRIP' && !acknowledged && !draft && (
          <div>
            <h2 className="text-lg font-semibold mb-4">Review prior inspection report (49 CFR 396.13)</h2>
            <div className="bg-white rounded-lg shadow p-4 mb-4">
              {priorDvir?.no_prior_report ? (
                <p className="text-gray-500 italic">No prior qualifying DVIR found for this vehicle.</p>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 mb-2">Prior DVIR — {formatRecordedDay(priorDvir?.shiftDate)}</p>
                  {priorDvir?.defects?.length ? (
                    <ul className="text-sm space-y-1">
                      {priorDvir.defects.map((d: any) => (
                        <li key={d.id} className="flex items-start gap-2">
                          <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5" />
                          <span>{d.description} {d.affectsSafety && <span className="text-red-600 font-medium">(safety concern)</span>}</span>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="text-sm text-gray-500">No defects reported.</p>}
                </div>
              )}
            </div>
            <label className="flex items-center gap-2 mb-2">
              <input type="checkbox" checked={ackChecked} onChange={(e) => setAckChecked(e.target.checked)} className="h-5 w-5" />
              {priorDvir?.no_prior_report ? (
                <span>No prior report exists for this vehicle — I confirm</span>
              ) : (
                <span>I have reviewed the prior inspection report.</span>
              )}
            </label>
            <label className="flex items-center gap-2 mb-4">
              <input type="checkbox" checked={acknowledgesSafe} onChange={(e) => setAcknowledgesSafe(e.target.checked)} className="h-5 w-5" />
              <span>I am satisfied the vehicle is in safe operating condition.</span>
            </label>
            <div className="flex gap-3">
              <button disabled={!ackChecked || !acknowledgesSafe || loading} onClick={() => signAcknowledgment(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 flex items-center gap-2">
                <FileSignature className="h-4 w-4" /> Sign acknowledgement & continue
              </button>
              <button onClick={() => signAcknowledgment(true)} disabled={loading || !concernNoteText.trim()}
                className="px-4 py-2 bg-amber-100 text-amber-800 rounded-lg disabled:opacity-50">
                Decline to operate / report concern
              </button>
            </div>
            {/* B13: concern note always visible on the refusal path — shown before it can be submitted */}
            <div className="mt-4">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Concern note (required for refusal)</label>
              <textarea
                value={concernNoteText}
                onChange={(e) => setConcernNoteText(e.target.value)}
                placeholder="State your concern about the vehicle's safe operating condition"
                className="w-full border rounded-lg p-2 text-sm"
                rows={2}
              />
            </div>
          </div>
        )}

        {/* Step 2b (pre-trip): acknowledgment signed — go straight to checklist */}
        {selectedAssignment && tab === 'PRE_TRIP' && acknowledged && !draft && (
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-3">Pre-trip DVIR for Unit {selectedAssignment.vehicle.unitNumber}. Acknowledgment recorded — start the checklist.</p>
            <button onClick={startChecklist} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4" /> Start inspection checklist
            </button>
          </div>
        )}

        {/* Step 2 (post-trip): no acknowledgment — go straight to checklist */}
        {selectedAssignment && tab === 'POST_TRIP' && !draft && (
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-3">Post-trip DVIR for Unit {selectedAssignment.vehicle.unitNumber}.</p>
            <button onClick={startChecklist} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4" /> Start inspection checklist
            </button>
          </div>
        )}

        {/* Step 3: Checklist */}
        {draft && (
          <div>
            {dotDraftRestored && (
              <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-4 py-2 text-sm flex items-center justify-between">
                <span>✓ Draft restored — your in-progress answers were recovered automatically.</span>
                <button onClick={() => { if (dotDraftKey) clearAutosave(dotDraftKey); window.location.reload(); }} className="text-xs underline text-emerald-700">Start over</button>
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Inspection checklist ({answeredCount}/{draft.snapshotItemCount})</h2>
              <div className="w-48 bg-gray-200 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${(answeredCount / draft.snapshotItemCount) * 100}%` }} />
              </div>
            </div>

            <div className="space-y-3">
              {draft.snapshotItems.map((item) => {
                const rk = rkey(item.item_id, item.tire_position);
                const r = responses[rk];
                return (
                  <div key={rk} className="bg-white rounded-lg shadow p-4">
                    {/* B12: hidden file input for real photo upload */}
                    <input
                      type="file"
                      accept="image/*"
                      ref={(el) => { fileInputRefs.current[rk] = el; }}
                      style={{ display: 'none' }}
                      onChange={(e) => handlePhotoSelect(item.item_id, item.tire_position, e)}
                    />
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="font-medium text-gray-900">{item.label}</div>
                        {item.reg_reference && <div className="text-xs text-gray-400">Ref: {item.reg_reference}</div>}
                        {item.photo_required && (
                          <div className="text-xs text-amber-700 mt-0.5 flex items-center gap-1">
                            <Camera className="h-3 w-3" /> Photo required ({item.photo_min}){item.photo_caption_required ? ' + reading' : ''}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Result buttons — no bulk-pass; each individually marked */}
                    <div className="flex gap-2 mb-3">
                      <button onClick={() => setResult(item.item_id, item.tire_position, 'PASS')}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium ${r?.result === 'PASS' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                        <Check className="h-4 w-4 inline mr-1" /> Pass
                      </button>
                      <button onClick={() => { setResult(item.item_id, item.tire_position, 'DEFECT'); setActiveDrawer({ itemId: item.item_id, tirePosition: item.tire_position, type: 'defect' }); }}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium ${r?.result === 'DEFECT' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                        <AlertTriangle className="h-4 w-4 inline mr-1" /> Defect
                      </button>
                      <button onClick={() => setResult(item.item_id, item.tire_position, 'NOT_APPLICABLE')}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium ${r?.result === 'NOT_APPLICABLE' ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                        N/A
                      </button>
                    </div>

                    {/* B9/B14: N/A reason input — required for NOT_APPLICABLE (pending supervisor/admin authorization) */}
                    {r?.result === 'NOT_APPLICABLE' && (
                      <div className="mb-3">
                        <input
                          value={r.naReason || ''}
                          onChange={(e) => setResponses((prev) => ({ ...prev, [rk]: { ...prev[rk], naReason: e.target.value } }))}
                          placeholder="N/A authorization reason (required — pending supervisor/admin approval)"
                          className="w-full border rounded-lg p-2 text-sm"
                        />
                        {naAuthorizedItems[rk] ? (
                          <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                            <Check className="h-3 w-3" /> N/A authorized — ready for sign-off.
                          </p>
                        ) : (['ADMIN', 'SAFETY_MANAGER', 'CONSTRUCTION_SUPERVISOR', 'PROJECT_MANAGER', 'CONSTRUCTION_MANAGER'].includes(user?.role)) ? (
                          <div className="mt-2">
                            <button
                              onClick={() => authorizeNa(item.item_id, item.tire_position)}
                              disabled={loading || !r.naReason?.trim()}
                              className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm disabled:opacity-50 flex items-center gap-1"
                            >
                              <Shield className="h-4 w-4" /> Authorize N/A
                            </button>
                            <p className="text-xs text-amber-600 mt-1">Authorize this N/A response so the driver can sign.</p>
                          </div>
                        ) : (
                          <p className="text-xs text-amber-600 mt-1">A supervisor/admin must authorize this N/A before sign-off. The DVIR is saved as DRAFT — click Sign &amp; Submit to attempt sign (it will succeed once authorized).</p>
                        )}
                      </div>
                    )}

                    {/* Photo evidence on every question (PASS or DEFECT) */}
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <button onClick={() => addPhoto(item.item_id, item.tire_position)} disabled={photoUploading} className="flex items-center gap-1 text-sm text-blue-600 hover:underline disabled:opacity-50">
                        <Camera className="h-4 w-4" /> {photoUploading ? 'Uploading…' : 'Camera'}
                      </button>
                      {r?.photos.map((p, idx) => (
                        <span key={idx} className="text-xs bg-gray-100 px-2 py-0.5 rounded flex items-center gap-1">
                          <Paperclip className="h-3 w-3" /> photo {idx + 1}
                          {item.photo_caption_required && (
                            <input value={r.captions[idx] || ''} onChange={(e) => setCaption(item.item_id, item.tire_position, idx, e.target.value)}
                              placeholder="reading (32nds)" className="w-24 text-xs border rounded px-1 py-0.5" />
                          )}
                        </span>
                      ))}
                    </div>

                    {/* Per-question action row (§7.4): flag / action-item / repair / comment / paperclip */}
                    <div className="flex gap-3 text-gray-500 text-sm border-t pt-2">
                      <button onClick={() => setActiveDrawer({ itemId: item.item_id, tirePosition: item.tire_position, type: 'flag' })} className="flex items-center gap-1 hover:text-amber-600">
                        <Flag className="h-4 w-4" /> Flag
                      </button>
                      {/* B12: Action-item symbol/control */}
                      <button onClick={() => setActiveDrawer({ itemId: item.item_id, tirePosition: item.tire_position, type: 'action' })} className="flex items-center gap-1 hover:text-orange-600">
                        <ListChecks className="h-4 w-4" /> Action
                      </button>
                      {/* B12/B14: Repair-clearance control */}
                      <button onClick={() => setActiveDrawer({ itemId: item.item_id, tirePosition: item.tire_position, type: 'repair' })} className="flex items-center gap-1 hover:text-blue-600">
                        <Wrench className="h-4 w-4" /> Repair
                      </button>
                      <button onClick={() => setActiveDrawer({ itemId: item.item_id, tirePosition: item.tire_position, type: 'comment' })} className="flex items-center gap-1 hover:text-blue-600">
                        <MessageSquare className="h-4 w-4" /> Comment
                      </button>
                      <button onClick={() => addPhoto(item.item_id, item.tire_position)} disabled={photoUploading} className="flex items-center gap-1 hover:text-gray-700 disabled:opacity-50">
                        <Paperclip className="h-4 w-4" /> Attach
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Odometer + GPS + Sign */}
            <div className="mt-6 bg-white rounded-lg shadow p-4">
              <label className="block text-sm font-medium mb-1">Odometer (mi)</label>
              <input type="number" value={odometer} onChange={(e) => setOdometer(e.target.value === '' ? '' : Number(e.target.value))}
                className="border rounded-lg p-2 w-40 mb-3" placeholder="12345" />
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                <MapPin className="h-4 w-4" /> GPS captured at sign (device, denied, or unavailable — recorded, not a gate)
              </div>
              <button disabled={!canSign || loading} onClick={sign}
                className="px-5 py-2.5 bg-green-600 text-white rounded-lg disabled:opacity-50 flex items-center gap-2">
                <FileSignature className="h-5 w-5" /> Sign & Submit
              </button>
              {!canSign && <p className="text-xs text-gray-400 mt-2">All {draft.snapshotItemCount} items must be answered and mandatory photos captured.</p>}
              {naPendingAuth && (
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-start gap-2">
                  <Shield className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <span>N/A items are pending supervisor/admin authorization. The DVIR is saved as DRAFT. Once a supervisor/admin authorizes the N/A responses, click Sign &amp; Submit again to complete sign-off.</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Defect drawer */}
        {activeDrawer?.type === 'defect' && (
          <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-20">
            <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-md p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">Report defect{activeDrawer.tirePosition ? ` — ${activeDrawer.tirePosition}` : ''}</h3>
                <button onClick={() => setActiveDrawer(null)}><X className="h-5 w-5" /></button>
              </div>
              {(() => { const dk = rkey(activeDrawer.itemId, activeDrawer.tirePosition); const r = responses[dk]; return (
                <>
              <textarea value={r?.defectDescription || ''}
                onChange={(e) => setResponses((prev) => ({ ...prev, [dk]: { ...prev[dk], defectDescription: e.target.value } }))}
                placeholder="Describe the defect (required)" className="w-full border rounded-lg p-2 text-sm mb-3" rows={3} />
              <label className="flex items-center gap-2 mb-2 text-sm">
                <input type="checkbox" checked={!!r?.affectsSafety}
                  onChange={(e) => setResponses((prev) => ({ ...prev, [dk]: { ...prev[dk], affectsSafety: e.target.checked } }))} />
                Affects safety
              </label>
              <label className="flex items-center gap-2 mb-3 text-sm">
                <input type="checkbox" checked={!!r?.couldCauseBreakdown}
                  onChange={(e) => setResponses((prev) => ({ ...prev, [dk]: { ...prev[dk], couldCauseBreakdown: e.target.checked } }))} />
                Could cause breakdown
              </label>
              <button onClick={() => submitDefect(activeDrawer.itemId, activeDrawer.tirePosition)} disabled={loading}
                className="w-full px-4 py-2 bg-red-600 text-white rounded-lg">Save defect</button>
                </>
              ); })()}
            </div>
          </div>
        )}

        {/* Flag (finding) drawer — B12: creates defect first, then finding */}
        {activeDrawer?.type === 'flag' && (
          <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-20">
            <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-md p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2"><Flag className="h-5 w-5 text-amber-500" /> Flag — finding{activeDrawer.tirePosition ? ` — ${activeDrawer.tirePosition}` : ''}</h3>
                <button onClick={() => setActiveDrawer(null)}><X className="h-5 w-5" /></button>
              </div>
              <input value={findingForm.title} onChange={(e) => setFindingForm({ ...findingForm, title: e.target.value })} placeholder="Finding title" className="w-full border rounded-lg p-2 text-sm mb-2" />
              <textarea value={findingForm.notes} onChange={(e) => setFindingForm({ ...findingForm, notes: e.target.value })} placeholder="Notes" className="w-full border rounded-lg p-2 text-sm mb-2" rows={2} />
              <input value={findingForm.closureComments} onChange={(e) => setFindingForm({ ...findingForm, closureComments: e.target.value })} placeholder="Closure comments (same-day Path A — optional)" className="w-full border rounded-lg p-2 text-sm mb-3" />
              <button onClick={() => submitFinding(activeDrawer.itemId, activeDrawer.tirePosition)} disabled={loading}
                className="w-full px-4 py-2 bg-amber-600 text-white rounded-lg">Create finding</button>
            </div>
          </div>
        )}

        {/* B12: Action-item drawer (Path B) — controlled inputs + real API call */}
        {activeDrawer?.type === 'action' && (
          <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-20">
            <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-md p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2"><ListChecks className="h-5 w-5 text-orange-500" /> Action item (Path B)</h3>
                <button onClick={() => setActiveDrawer(null)}><X className="h-5 w-5" /></button>
              </div>
              <p className="text-xs text-gray-500 mb-3">A DefectActionItem will be created from the defect for this question to track follow-up resolution.</p>
              <input
                value={actionItemForm.correctiveAction}
                onChange={(e) => setActionItemForm({ ...actionItemForm, correctiveAction: e.target.value })}
                placeholder="Corrective action (what will be done)"
                className="w-full border rounded-lg p-2 text-sm mb-2"
              />
              <input
                type="date"
                value={actionItemForm.deadline}
                onChange={(e) => setActionItemForm({ ...actionItemForm, deadline: e.target.value })}
                placeholder="Deadline"
                className="w-full border rounded-lg p-2 text-sm mb-2"
              />
              <input
                type="number"
                value={actionItemForm.responsiblePersonId}
                onChange={(e) => setActionItemForm({ ...actionItemForm, responsiblePersonId: e.target.value })}
                placeholder="Responsible person ID"
                className="w-full border rounded-lg p-2 text-sm mb-3"
              />
              <button
                onClick={() => submitActionItem(activeDrawer.itemId, activeDrawer.tirePosition)}
                disabled={loading}
                className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg"
              >Create action item</button>
            </div>
          </div>
        )}

        {/* B12/B14: Repair-clearance drawer (mechanic UI) — controlled inputs + real API + photo upload */}
        {activeDrawer?.type === 'repair' && (
          <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-20">
            <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2"><Wrench className="h-5 w-5 text-blue-500" /> Record repair clearance</h3>
                <button onClick={() => setActiveDrawer(null)}><X className="h-5 w-5" /></button>
              </div>
              <p className="text-xs text-amber-700 bg-amber-50 rounded p-2 mb-3">
                Conformara records the repair certification as entered. It does not verify that the mechanic is qualified under 49 CFR 396.19.
              </p>
              <input
                value={repairForm.repairDescription}
                onChange={(e) => setRepairForm({ ...repairForm, repairDescription: e.target.value })}
                placeholder="Repair description (required)"
                className="w-full border rounded-lg p-2 text-sm mb-2"
              />
              <input
                value={repairForm.partsUsed}
                onChange={(e) => setRepairForm({ ...repairForm, partsUsed: e.target.value })}
                placeholder="Parts used (optional)"
                className="w-full border rounded-lg p-2 text-sm mb-2"
              />
              <textarea
                value={repairForm.closureComments}
                onChange={(e) => setRepairForm({ ...repairForm, closureComments: e.target.value })}
                placeholder="Closure comments (required for Path A — REPAIRED)"
                className="w-full border rounded-lg p-2 text-sm mb-2"
                rows={2}
              />
              {/* B12/B14: Real repair photo upload via multipart */}
              <div className="mb-2">
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  ref={(el) => { (repairFileRef as any).current = el; }}
                  onChange={(e) => draft && handleRepairPhotoSelect(draft.dvir.id, e)}
                />
                <button
                  onClick={() => (repairFileRef as any).current?.click()}
                  disabled={photoUploading}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:underline disabled:opacity-50 mb-1"
                >
                  <Camera className="h-4 w-4" /> {photoUploading ? 'Uploading…' : 'Upload repair photo'}
                </button>
                {repairForm.repairPhotoKeys.length > 0 && (
                  <p className="text-xs text-green-600">{repairForm.repairPhotoKeys.length} photo(s) uploaded</p>
                )}
              </div>
              <select
                value={repairForm.disposition}
                onChange={(e) => setRepairForm({ ...repairForm, disposition: e.target.value as any })}
                className="w-full border rounded-lg p-2 text-sm mb-2"
              >
                <option value="REPAIRED">Repaired</option>
                <option value="REPAIR_UNNECESSARY">Repair unnecessary</option>
                <option value="NOT_CLEARED">Not cleared</option>
              </select>
              <textarea
                value={repairForm.mechanicAttestationText}
                onChange={(e) => setRepairForm({ ...repairForm, mechanicAttestationText: e.target.value })}
                placeholder="Mechanic attestation text (required)"
                className="w-full border rounded-lg p-2 text-sm mb-2"
                rows={2}
              />
              <label className="flex items-center gap-2 mb-3 text-sm">
                <input
                  type="checkbox"
                  checked={repairForm.mechanicAttestsVehicleSafe}
                  onChange={(e) => setRepairForm({ ...repairForm, mechanicAttestsVehicleSafe: e.target.checked })}
                />
                I attest the vehicle is safe to operate
              </label>
              <button
                onClick={() => submitRepairClearance(activeDrawer.itemId, activeDrawer.tirePosition)}
                disabled={loading}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg"
              >Record clearance</button>
            </div>
          </div>
        )}

        {/* Comment drawer */}
        {activeDrawer?.type === 'comment' && (
          <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-20">
            <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-md p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2"><MessageSquare className="h-5 w-5 text-blue-500" /> Comment</h3>
                <button onClick={() => setActiveDrawer(null)}><X className="h-5 w-5" /></button>
              </div>
              <textarea value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Note about this question" className="w-full border rounded-lg p-2 text-sm mb-3" rows={3} />
              <button onClick={() => submitComment(activeDrawer.itemId)} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg">Add comment</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default DotInspectionPage;
